// Property analytics field mapping and aggregation.
//
// Verified against a real GET /get_analytics_data response. The API returns
// per-inspection records plus a few server-side totals; every average and
// distribution shown on the page is derived here from `data`, matching what
// the existing portal displays for the same payload.
//
// inspection_duration arrives in SECONDS (142.0 renders as "2.4 min").

const DURATION_BUCKETS = [
  { label: "< 2 min", min: 0, max: 120 },
  { label: "2–5 min", min: 120, max: 300 },
  { label: "5–10 min", min: 300, max: 600 },
  { label: "10–20 min", min: 600, max: 1200 },
  { label: "20+ min", min: 1200, max: Infinity },
];

const PHOTO_BUCKETS = [
  { label: "1–5", min: 1, max: 5 },
  { label: "6–10", min: 6, max: 10 },
  { label: "11–15", min: 11, max: 15 },
  { label: "16–20", min: 16, max: 20 },
  { label: "21+", min: 21, max: Infinity },
];

// null/undefined/"" must stay null rather than becoming 0 — Number() coerces
// all three to 0, which would quietly count unrated inspections as zero-star
// and drag the average rating down.
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const mean = (values) =>
  values.length === 0 ? null : values.reduce((a, b) => a + b, 0) / values.length;

export const toPropertyAnalyticsRecord = (raw) => ({
  refNum: raw?.ref_num ?? "",
  address: raw?.address ?? "",
  createdAt: raw?.created_at ?? null,
  claimState: raw?.claim_state ?? null,
  caseStatus: raw?.case_status ?? "",
  // seconds on the wire; minutes is what the UI wants
  durationSeconds: num(raw?.inspection_duration),
  photos: num(raw?.photos_uploaded) ?? 0,
  rating: num(raw?.trinetra_rating),
  comment: raw?.trinetra_comment ?? "",
  warnings: Array.isArray(raw?.warnings)
    ? raw.warnings
        .map((w) => w?.warning_message)
        .filter(Boolean)
    : [],
});

export const toPropertyAnalytics = (response) => {
  const records = Array.isArray(response?.data)
    ? response.data.map(toPropertyAnalyticsRecord)
    : [];

  const durations = records.map((r) => r.durationSeconds).filter((v) => v != null);
  const photos = records.map((r) => r.photos).filter((v) => v != null);
  // Only rated inspections count towards the average — unrated ones are not
  // zeros, they simply have no rating.
  const ratings = records.map((r) => r.rating).filter((v) => v != null);

  const bucketCounts = (buckets, values) =>
    buckets.map(({ label, min, max }) => ({
      label,
      count: values.filter((v) => v >= min && v <= (max === Infinity ? Infinity : max)).length,
    }));

  const durationDistribution = DURATION_BUCKETS.map(({ label, min, max }) => ({
    label,
    count: durations.filter((v) => v >= min && v < max).length,
  }));

  const ratingDistribution = [1, 2, 3, 4, 5].map((star) => ({
    label: `★ ${star}`,
    count: ratings.filter((v) => Math.round(v) === star).length,
  }));

  const links = response?.trinetra_links_summary || {};

  return {
    records,
    totals: {
      total: num(response?.total_count) ?? records.length,
      completed: num(response?.passed_count) ?? 0,
      failed: num(response?.failed_count) ?? 0,
      avgDurationMinutes: durations.length ? mean(durations) / 60 : null,
      avgPhotos: photos.length ? mean(photos) : null,
      avgRating: ratings.length ? mean(ratings) : null,
    },
    durationDistribution,
    photoDistribution: bucketCounts(PHOTO_BUCKETS, photos),
    ratingDistribution,
    failedReasons: Object.entries(response?.failed_reason_breakdown || {}).map(
      ([reason, count]) => ({ reason, count: num(count) ?? 0 }),
    ),
    links: {
      total: num(links.total_links_generated) ?? 0,
      completed: num(links.completed) ?? 0,
      startedNotCompleted: num(links.started_not_completed) ?? 0,
      notStarted: num(links.not_started) ?? 0,
      expired: num(links.expired) ?? 0,
      rows: Array.isArray(links.links_data)
        ? links.links_data.map((l) => ({
            refNum: l?.ref_num ?? "",
            ergoId: l?.ergoid ?? "",
            createdAt: l?.created_at ?? null,
            status: l?.status ?? "",
            stepStopped: l?.step_stopped ?? null,
            aiResult: l?.ai_result ?? null,
          }))
        : [],
    },
  };
};
